import axiosInstance from "../utils/axios/axios";
import privateAxiosInstance from "../utils/axios/private";
import { hybridEncrypt, hybridDecrypt } from "../utils/crypto";

export const clientApi = {
    /**
     * Client Onboarding API
     * Endpoint: /client/onboard/
     * Method: POST
     * @param {object} payload - Client onboarding details
     */
    async registerCompany(payload) {
        try {
            const encryptedPayload = await hybridEncrypt(payload);

            const response = await privateAxiosInstance.post(
                "/tenant/register/company/",
                encryptedPayload
            );

            let finalData = response.data;

            if (response.data?.encrypted) {
                finalData = await hybridDecrypt(response.data);
            }

            return {
                status: response.status,
                data: finalData,
                message: finalData?.message,
                success: true,
            };

        } catch (error) {

            if (error.code === "ERR_NETWORK") {
                return {
                    status: 0,
                    data: null,
                    message: "NETWORK_ERROR",
                    success: false,
                };
            }

            return {
                status: error?.response?.status,
                data: error?.response?.data,
                message: error?.response?.data?.message,
                success: false,
            };
        }
    },

    async registerFreelancer(payload) {
        try {
            const encryptedPayload = await hybridEncrypt(payload);

            const response = await privateAxiosInstance.post(
                "/tenant/register/freelancer/",
                encryptedPayload
            );

            let finalData = response.data;

            if (response.data?.encrypted) {
                finalData = await hybridDecrypt(response.data);
            }

            return {
                status: response.status,
                data: finalData,
                message: finalData?.message,
                success: true,
            };

        } catch (error) {

            if (error.code === "ERR_NETWORK") {
                return {
                    status: 0,
                    data: null,
                    message: "NETWORK_ERROR",
                    success: false,
                };
            }

            return {
                status: error?.response?.status,
                data: error?.response?.data,
                message: error?.response?.data?.message,
                success: false,
            };
        }
    },


async registerGoogleFreelancer(payload) {

    try {

        const encryptedPayload = await hybridEncrypt(payload);

        const response = await privateAxiosInstance.post(
            "/tenant/register/freelancer/google/",
            encryptedPayload
        );

        let finalData = response.data;

        if (response.data?.encrypted) {
            finalData = await hybridDecrypt(response.data);
        }

        return {
            status: response.status,
            data: finalData,
            message: finalData?.message,
            success: true,
        };

    } catch (error) {

        if (error.code === "ERR_NETWORK") {
            return {
                status: 0,
                data: null,
                message: "NETWORK_ERROR",
                success: false,
            };
        }

        return {
            status: error?.response?.status,
            data: error?.response?.data,
            message: error?.response?.data?.message,
            success: false,
        };
    }
},


    /**
     * Get Tenant Details API
     * Endpoint: /client/tenantDetails/
     * Method: GET
     */
    async getTenantToken(payload) {
        try {
            const response = await privateAxiosInstance.post("/tenant/detail/", payload);
            if (response.data.encrypted) {
                return await hybridDecrypt(response.data);
            }
            return response.data
        } catch (error) {
            if (error.code === "ERR_NETWORK") {
                return {
                    status: 0,
                    data: "Network_error",
                    message: "Network error. Please check your internet connection or try again later.",
                    type: "network_error",
                }
            }
            return error.response.data
        }
    },

    async getTenants({ page = 1, page_size = 5 } = {}) {
        try {
            const response = await privateAxiosInstance.get("/tenant/", {
                params: { page, page_size },
            });
            if (response.data.encrypted) {
                return await hybridDecrypt(response.data);
            }
            return response.data;
        } catch (error) {
            if (error.code === "ERR_NETWORK") {
                return {
                    status: 0,
                    data: null,
                    message: "Network error. Please check your internet connection or try again later.",
                    type: "network_error",
                };
            }
            return error.response?.data;
        }
    },

    async tenantApproval(payload) {
        try {
            const encrypted_payload = await hybridEncrypt(payload)
            const response = await privateAxiosInstance.post("/tenant/approval/", encrypted_payload);
            if (response.data.encrypted) {
                return await hybridDecrypt(response.data);
            }
            return response.data
        } catch (error) {
            if (error.code === "ERR_NETWORK") {
                return {
                    status: 0,
                    data: "Network_error",
                    message: "Network error. Please check your internet connection or try again later.",
                    type: "network_error",
                }
            }
            return error.response.data
        }
    },

    /**
     * Upload Tenant Logo
     * Endpoint: /tenant/upload-logo/
     * Method: POST (FormData)
     * @param {File} logoFile - Logo image file
     */
    async uploadLogo(formData) {
        try {
            const response = await privateAxiosInstance.post("/tenant/logo/", formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            if (response.data.encrypted) {
                return await hybridDecrypt(response.data);
            }
            return response.data;
        } catch (error) {
            if (error.code === "ERR_NETWORK") {
                return {
                    status: 0,
                    data: "Network_error",
                    message: "Network error. Please check your internet connection or try again later.",
                    type: "network_error",
                }
            }
            return error.response?.data || { status: 500, message: "Error uploading logo" };
        }
    },

    /**
     * Get Tenant Logo
     * Endpoint: /tenant/logo/
     * Method: GET
     */
    async getLogo() {
        try {
            const response = await privateAxiosInstance.get("/tenant/logo/");
            if (response.data.encrypted) {
                return await hybridDecrypt(response.data);
            }
            return response.data;
        } catch (error) {
            if (error.code === "ERR_NETWORK") {
                return {
                    status: 0,
                    data: "Network_error",
                    message: "Network error. Please check your internet connection or try again later.",
                    type: "network_error",
                }
            }
            return error.response?.data || { status: 404, message: "Logo not found" };
        }
    },

    /**
     * Update Tenant API
     * Endpoint: /tenant/update/
     * Method: PATCH
     * @param {object} payload - Tenant update details
     */
    async updateTenant(payload) {
        try {
            const encryptedPayload = await hybridEncrypt(payload);
            const response = await privateAxiosInstance.patch(
                "/tenant/update/",
                encryptedPayload
            );

            let finalData = response.data;

            if (response.data?.encrypted) {
                finalData = await hybridDecrypt(response.data);
            }

            return {
                status: response.status,
                data: finalData,
                message: finalData?.message,
                success: true,
            };
        } catch (error) {
            if (error.code === "ERR_NETWORK") {
                return {
                    status: 0,
                    data: null,
                    message: "NETWORK_ERROR",
                    success: false,
                };
            }

            return {
                status: error?.response?.status,
                data: error?.response?.data,
                message: error?.response?.data?.message,
                success: false,
            };
        }
    },

    /**
     * Send OTP to email for registration verification
     * Endpoint: /tenant/otp/send/
     * Method: POST
     * @param {Object} payload - { email, user_type, full_name }
     */
    async sendRegistrationOtp(payload) {
        try {
            const encryptedPayload = await hybridEncrypt(payload);
            const response = await privateAxiosInstance.post(
                "/tenant/otp/send/",
                encryptedPayload
            );

            let finalData = response.data;
            if (response.data?.encrypted) {
                finalData = await hybridDecrypt(response.data);
            }

            return {
                status: response.status,
                data: finalData,
                message: finalData?.message,
                success: true,
            };
        } catch (error) {
            if (error.code === "ERR_NETWORK") {
                return {
                    status: 0,
                    data: null,
                    message: "Network error. Please check your internet connection.",
                    success: false,
                };
            }
            return {
                status: error?.response?.status,
                data: error?.response?.data,
                message: error?.response?.data?.message,
                success: false,
            };
        }
    },

    /**
     * Verify the OTP entered by the user during registration
     * Endpoint: /tenant/otp/verify/
     * Method: POST
     * @param {Object} payload - { email, otp }
     */
    async verifyRegistrationOtp(payload) {
        try {
            const encryptedPayload = await hybridEncrypt(payload);
            const response = await privateAxiosInstance.post(
                "/tenant/otp/verify/",
                encryptedPayload
            );

            let finalData = response.data;
            if (response.data?.encrypted) {
                finalData = await hybridDecrypt(response.data);
            }

            return {
                status: response.status,
                data: finalData,
                message: finalData?.message,
                success: true,
            };
        } catch (error) {
            if (error.code === "ERR_NETWORK") {
                return {
                    status: 0,
                    data: null,
                    message: "Network error. Please check your internet connection.",
                    success: false,
                };
            }
            return {
                status: error?.response?.status,
                data: error?.response?.data,
                message: error?.response?.data?.message,
                success: false,
            };
        }
    },

    async checkAvailability(payload) {
        try {
            const encryptedPayload = await hybridEncrypt(payload);
            const response = await privateAxiosInstance.post(
                "/tenant/validate/",
                encryptedPayload
            );

            let finalData = response.data;
            if (response.data?.encrypted) {
                finalData = await hybridDecrypt(response.data);
            }

            return {
                status: response.status,
                data: finalData,
                message: finalData?.message,
                success: true,
            };
        } catch (error) {
            if (error.code === "ERR_NETWORK") {
                return {
                    status: 0,
                    data: null,
                    message: "Network error. Please check your internet connection.",
                    success: false,
                };
            }
            return {
                status: error?.response?.status,
                data: error?.response?.data,
                message: error?.response?.data?.message,
                success: false,
            };
        }
    },
};
